import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Pencil } from 'lucide-react';

import type { FieldStatus } from '../api';

interface ConfidenceMeterProps {
  score: number;
  status: FieldStatus;
  /** When true, replaces the bar with a pencil chip to mark a manual edit. */
  manuallyEdited?: boolean;
}

// Brand confidence semantics (landing --ok/--warn/--bad) — solid fills like the
// landing's meters. Warn keeps amber-800 text for AA contrast.
const STATUS: Record<FieldStatus, { bar: string; ring: string; text: string; track: string }> = {
  ok: {
    bar: 'bg-success',
    ring: 'ring-success/30',
    text: 'text-success',
    track: 'bg-success/10',
  },
  warning: {
    bar: 'bg-warning',
    ring: 'ring-warning/40',
    text: 'text-amber-800',
    track: 'bg-warning/10',
  },
  critical: {
    bar: 'bg-danger',
    ring: 'ring-danger/30',
    text: 'text-danger',
    track: 'bg-danger/10',
  },
};

/**
 * Linear confidence indicator (0–100%) with animated fill on mount/update. Sits where
 * the categorical circle used to be; the proportional bar reads more accurately and
 * makes deltas between fields obvious at a glance.
 *
 * Manual edits short-circuit to a pencil chip — same semantics as the previous
 * `ScoreBadge`, but with a touch more visual emphasis.
 */
export function ConfidenceMeter({ score, status, manuallyEdited = false }: ConfidenceMeterProps): JSX.Element {
  const target = Math.max(0, Math.min(100, Math.round(score * 100)));

  // Two-frame trick to animate from 0 → target on mount: render the bar at 0% on the
  // first paint, then bump to the real value so the CSS transition kicks in.
  const [animated, setAnimated] = useState<number>(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setAnimated(target));
    return () => cancelAnimationFrame(raf);
  }, [target]);

  if (manuallyEdited) {
    return (
      <span
        role="img"
        aria-label="Editado manualmente"
        className="inline-flex h-9 w-20 items-center justify-center gap-1 rounded-full bg-primary/10 text-xs font-semibold text-primary ring-1 ring-primary/30 transition-transform hover:-translate-y-0.5"
        title="Editado manualmente"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} />
        Editado
      </span>
    );
  }

  const tone = STATUS[status];
  return (
    <div
      className="flex w-20 flex-col items-stretch gap-1"
      aria-label={`Confianza ${target}% (${status})`}
    >
      <div className={clsx('h-1.5 w-full overflow-hidden rounded-full ring-1', tone.track, tone.ring)}>
        <div
          className={clsx('h-full rounded-full transition-[width] duration-700 ease-out', tone.bar)}
          style={{ width: `${animated}%` }}
        />
      </div>
      <span className={clsx('font-mono text-[11px] font-semibold tabular-nums leading-none', tone.text)}>
        {target}%
      </span>
    </div>
  );
}
